/**
 * ProviderBreakdown - Financial overview by provider
 */

import {
  ArrowDownIcon,
  ArrowUpIcon,
  BuildingIcon,
  ChevronRightIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  ProviderBreakdown as ProviderBreakdownType,
  useGetProviderBreakdownQuery,
} from '@/services/api/ledgerService';

import { formatCurrency } from '../shared/formatUtils';
import { LedgerEmptyState } from '../shared/LedgerEmptyState';

interface ProviderBreakdownProps {
  matterId: string;
  onProviderClick?: (providerId: string) => void;
}

type SortDirection = 'asc' | 'desc';

/** Skeleton loader */
const ProviderSkeleton = () => (
  <div className="divide-y divide-slate-100">
    {[1, 2, 3].map((i) => (
      <div key={i} className="flex items-center justify-between py-3 px-3">
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-8 rounded-md" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="flex items-center gap-6">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
        </div>
      </div>
    ))}
  </div>
);

export const ProviderBreakdown = ({
  matterId,
  onProviderClick,
}: ProviderBreakdownProps) => {
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const { data, isLoading, isError } = useGetProviderBreakdownQuery({
    matterId,
  });

  // API returns array but codegen types it wrong - cast to array
  const providers = useMemo(() => {
    if (!data) return [];
    // Handle both array and single object cases
    const arr = Array.isArray(data)
      ? (data as ProviderBreakdownType[])
      : [data as ProviderBreakdownType];
    return [...arr].sort((a, b) => {
      const aValue = parseFloat(a.total_billed) || 0;
      const bValue = parseFloat(b.total_billed) || 0;
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [data, sortDirection]);

  const toggleSort = () =>
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));

  if (isLoading) return <ProviderSkeleton />;

  if (isError) {
    return (
      <LedgerEmptyState
        title="Failed to load providers"
        description="There was an error loading provider data."
      />
    );
  }

  if (providers.length === 0) {
    return (
      <LedgerEmptyState
        title="No providers yet"
        description="Add expenses to see provider breakdown."
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50/50 border-b border-slate-100">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
          Provider
        </span>
        <div className="flex items-center gap-6 text-xs font-medium text-slate-500 uppercase tracking-wide">
          <button
            type="button"
            onClick={toggleSort}
            className="flex items-center gap-1 hover:text-slate-700 transition-colors"
          >
            Total Billed
            {sortDirection === 'desc' ? (
              <ArrowDownIcon className="h-3 w-3 text-blue-500" />
            ) : (
              <ArrowUpIcon className="h-3 w-3 text-blue-500" />
            )}
          </button>
          <span className="w-20 text-right">PIP</span>
          <span className="w-20 text-right">MedPay</span>
          <span className="w-20 text-right">Unalloc.</span>
          <span className="w-24 text-right">Lien</span>
        </div>
      </div>

      {/* Provider Rows */}
      <div className="divide-y divide-slate-100">
        {providers.map((provider) => {
          const lienExposure = parseFloat(provider.lien_exposure) || 0;
          const unallocated = parseFloat(provider.by_account.unallocated) || 0;

          return (
            <button
              key={provider.provider_id as string}
              type="button"
              onClick={() => onProviderClick?.(provider.provider_id as string)}
              className={cn(
                'w-full flex items-center justify-between py-3 px-3',
                'hover:bg-slate-50 transition-colors text-left',
                'focus:outline-none focus:bg-slate-50',
                onProviderClick && 'cursor-pointer',
              )}
            >
              {/* Provider Info */}
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-100 flex-shrink-0">
                  <BuildingIcon className="h-4 w-4 text-slate-500" />
                </div>
                <p className="text-sm font-medium text-slate-900 truncate">
                  {provider.provider.name}
                </p>
              </div>

              {/* Metrics */}
              <div className="flex items-center gap-6">
                <span className="w-20 text-right text-sm font-semibold text-slate-900">
                  {formatCurrency(provider.total_billed)}
                </span>
                <span className="w-20 text-right text-sm text-slate-600">
                  {formatCurrency(provider.by_account.pip_paid)}
                </span>
                <span className="w-20 text-right text-sm text-slate-600">
                  {formatCurrency(provider.by_account.medpay_paid)}
                </span>
                <span
                  className={cn(
                    'w-20 text-right text-sm',
                    unallocated > 0
                      ? 'text-amber-600 font-medium'
                      : 'text-slate-400',
                  )}
                >
                  {formatCurrency(unallocated)}
                </span>
                <div className="w-24 flex items-center justify-end gap-2">
                  {provider.has_lien ? (
                    <>
                      <Badge
                        variant="outline"
                        className="bg-red-50 text-red-700 border-red-200 text-xs"
                      >
                        Lien
                      </Badge>
                      <span className="text-sm font-medium text-red-600">
                        {formatCurrency(lienExposure)}
                      </span>
                    </>
                  ) : (
                    <span className="text-sm text-slate-400">—</span>
                  )}
                </div>
                {onProviderClick && (
                  <ChevronRightIcon className="h-4 w-4 text-slate-300 flex-shrink-0" />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
